import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { DeviceStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SearchInput } from '../../components/ui/Input.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { ConfirmDialog } from '../../components/ui/Modal.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchDevices, revokeDevice } from '../../api/devices.js';
import { formatRelativeTime } from '../../lib/utils.js';
import type { Device } from '@hisob/types';

export function DevicesPage() {
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [deviceToRevoke, setDeviceToRevoke] = useState<Device | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-devices', { cursor, platform: platformFilter }],
    queryFn: () => fetchDevices({ limit: 15, cursor, platform: platformFilter !== 'all' ? platformFilter : undefined }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeDevice(id),
    onSuccess: () => {
      success(t('devices.revokeSuccessToast'));
      setDeviceToRevoke(null);
      queryClient.invalidateQueries({ queryKey: ['admin-devices'] });
      queryClient.invalidateQueries({ queryKey: ['overview-metrics'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to revoke device', err.requestId);
    },
  });

  const filteredDevices = (data?.items || []).filter((d) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      d.id.toLowerCase().includes(term) ||
      d.user_id.toLowerCase().includes(term) ||
      d.app_version.toLowerCase().includes(term) ||
      (d.device_model && d.device_model.toLowerCase().includes(term))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('devices.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('devices.description')}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-3">
          <div className="w-full sm:w-72">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('devices.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t('devices.allPlatforms')}</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
              <option value="web">Web</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Devices Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={6} cols={6} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('devices.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredDevices.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('devices.emptyTitle')}
                description={t('devices.emptyDesc')}
                actionText={t('common.resetFilters')}
                onAction={() => {
                  setSearch('');
                  setPlatformFilter('all');
                }}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('devices.tableDeviceId')}</th>
                  <th className="py-3 px-4">{t('devices.tableUserId')}</th>
                  <th className="py-3 px-4">{t('devices.tableModelPlatform')}</th>
                  <th className="py-3 px-4">{t('devices.tableAppVersion')}</th>
                  <th className="py-3 px-4">{t('devices.tableStatus')}</th>
                  <th className="py-3 px-4">{t('devices.tableLastSeen')}</th>
                  <th className="py-3 px-4 text-right">{t('devices.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                {filteredDevices.map((device) => {
                  const isOnline = Boolean(
                    device.last_seen_at && Date.now() / 1000 - device.last_seen_at < 300
                  );
                  const isRevoked = Boolean(device.revoked_at);

                  return (
                    <tr
                      key={device.id}
                      onClick={() => navigate(`/admin/devices/${device.id}`)}
                      className="hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4">
                        <CopyButton value={device.id} truncate={true} className="font-mono font-medium text-indigo-600 dark:text-indigo-400" />
                      </td>
                      <td className="py-3 px-4">
                        <CopyButton value={device.user_id} truncate={true} className="font-mono text-slate-500" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-slate-900 dark:text-slate-100">
                          {device.device_model || 'Unknown Device'}
                        </div>
                        <div className="text-[11px] text-slate-400 uppercase">
                          {device.platform} • {device.os_version || 'OS N/A'}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-700 dark:text-slate-300">
                        v{device.app_version}
                      </td>
                      <td className="py-3 px-4">
                        <DeviceStatusBadge isOnline={isOnline} isRevoked={isRevoked} />
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {formatRelativeTime(device.last_seen_at)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/admin/devices/${device.id}`)}
                          >
                            {t('common.details')}
                          </Button>

                          {!isRevoked && (
                            <PermissionGuard permission="devices:revoke">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 border-rose-200 dark:border-rose-900/60"
                                onClick={() => setDeviceToRevoke(device)}
                              >
                                {t('devices.revokeButton')}
                              </Button>
                            </PermissionGuard>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>

        {/* Cursor Pagination Controls */}
        {data && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500">
            <span>{t('devices.totalCount', { count: filteredDevices.length })}</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!cursor}
                onClick={() => setCursor(undefined)}
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t('common.firstPage')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data.has_more || !data.next_cursor}
                onClick={() => setCursor(data.next_cursor || undefined)}
              >
                {t('common.nextPage')} <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Revoke Confirmation Modal with 2-step verification */}
      {deviceToRevoke && (
        <ConfirmDialog
          isOpen={Boolean(deviceToRevoke)}
          onClose={() => setDeviceToRevoke(null)}
          onConfirm={() => revokeMutation.mutate(deviceToRevoke.id)}
          title={t('devices.revokeModalTitle')}
          message={t('devices.revokeModalMessage', { id: deviceToRevoke.id })}
          confirmText={t('devices.revokeConfirmText')}
          variant="danger"
          requireVerificationText="REVOKE"
          isLoading={revokeMutation.isPending}
        />
      )}
    </div>
  );
}
